import type React from 'react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '@/features/Overview/utils/productEntries';
import EmployeeRow from '@/features/Quote/components/settings/employees/Form';
import type { AppData } from '@/types/app';
import SectionCard from './SectionCard';

interface EmployeesSectionProps {
	appData: AppData;
}

// Matches the grid template of the flow's editable employee row (Form.tsx) so the
// header columns line up with the disabled fields below.
const GRID_COLUMNS = 'sm:grid-cols-[60px_2fr_1.5fr_1.5fr_2fr]';

/**
 * "Medewerkers" — reuses the flow's own employee row (`Form`) rendered read-only
 * (fields disabled, delete hidden), so the table matches the main flow exactly.
 */
const EmployeesSection = ({ appData }: EmployeesSectionProps): React.JSX.Element => {
	const { t } = useTranslation();
	// EmployeeRow writes focus refs here; unused in read-only mode but required by its API.
	const nameRefs = useRef<Record<number, HTMLInputElement | null>>({});
	const employeeList = appData.premium_variables?.employees?.employee_list ?? [];
	const totalWage = employeeList.reduce(
		(sum, e) => sum + (typeof e.wage === 'number' ? e.wage : 0),
		0
	);

	// Employees have no stable id and rows can be identical, so derive a unique key
	// per row from its fields plus an occurrence counter (avoids using the array index).
	const seenKeys = new Map<string, number>();
	const rows = employeeList.map((employee) => {
		const base = `${employee.id ?? ''}|${employee.name ?? ''}|${employee.birth_date ?? ''}|${employee.wage ?? ''}|${employee.contract_type ?? ''}`;
		const occurrence = seenKeys.get(base) ?? 0;
		seenKeys.set(base, occurrence + 1);
		return { employee, key: `${base}#${occurrence}` };
	});

	return (
		<SectionCard
			title={t('quotes.settings.employees.title')}
			testId="dossier-section-employees"
			bodyClassName="p-8 sm:p-9"
		>
			{employeeList.length === 0 ? (
				<p className="text-sm italic text-muted-foreground/70">
					{t('requestOverview.premiumVars.noEmployees')}
				</p>
			) : (
				<>
					{/* Column headers (desktop) aligned to the reused row's grid. */}
					<div
						className={`hidden sm:grid ${GRID_COLUMNS} gap-2 w-full mb-2 px-0 text-xs font-medium text-muted-foreground`}
					>
						<span>{t('quotes.settings.employees.gender')}</span>
						<span>{t('quotes.settings.employees.name')}</span>
						<span>{t('quotes.settings.employees.birthdate')}</span>
						<span>{t('quotes.settings.employees.wage')}</span>
						<span>{t('quotes.settings.employees.contractType')}</span>
					</div>

					{rows.map(({ employee, key }, idx) => (
						<EmployeeRow key={key} employee={employee} idx={idx} nameRefs={nameRefs} readOnly />
					))}

					<div className="mt-4 flex flex-wrap gap-x-8 gap-y-1 border-t border-border/70 pt-3 text-sm">
						<span className="text-muted-foreground">
							{t('requestOverview.premiumVars.employeeCount')}:{' '}
							<span className="font-semibold text-foreground">{employeeList.length}</span>
						</span>
						{totalWage > 0 && (
							<span className="text-muted-foreground">
								{t('requestOverview.premiumVars.totalWage')}:{' '}
								<span className="font-semibold text-foreground">{formatCurrency(totalWage)}</span>
							</span>
						)}
					</div>
				</>
			)}
		</SectionCard>
	);
};

export default EmployeesSection;
